import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getSessionsARappeler, supprimerSession } from '../services/sessionService';
import { formatDate } from './devisUtils';
import ModeleEmailPanneau from './ModeleEmailPanneau';
import SessionsPanel from './SessionsPanel';
import ActionsLigne from '../components/ActionsLigne';
import ConfirmationSuppression from '../components/ConfirmationSuppression';
import { toast } from '../components/Toasts';
import FiltreDossier from './FiltreDossier';
import StatutsBarre from './StatutsBarre';
import { FACETTES_RAPPEL, FACETTES_ENVOI_RAPPEL } from './facettes';
import FiltresRepliables from '../components/FiltresRepliables';
import '../assets/styles/Devis.css';
import '../assets/styles/DemandeDevis.css';
import '../assets/styles/Listes.css';

const LIMIT = 15;

/**
 * Les échéances du planificateur, dans l'ordre où elles tombent.
 *
 * La page ne déclenche plus rien : elle dit ce qui est parti. Les clés sont
 * celles de rappelFormationService.ECHEANCES, telles que la trace les écrit.
 */
/**
 * Les textes que cette vue gouverne.
 *
 * Chaque échéance a deux versions : celle des élus et celle du formateur
 * (migration 062). La vue des élus donne accès aux premières, celle des
 * formateurs aux secondes — on règle le message là où on suit ceux qui le
 * reçoivent, comme la convocation client et la convocation formateur.
 */
const MODELES = {
  // Un seul texte de rappel, servi à chaque délai réglé dans Paramétrage :
  // la balise {{quand}} dit « demain », « dans une semaine »…
  eleves: [
    { type: 'formation_rappel', court: 'Rappel' },
    { type: 'formation_jour_j', court: 'Jour J' },
    { type: 'emargement_relance', court: 'Relance' },
    { type: 'formation_satisfaction', court: 'Satisfaction' },
  ],
  formateur: [
    { type: 'formation_rappel_formateur', court: 'Rappel' },
    { type: 'formation_jour_j_formateur', court: 'Jour J' },
    { type: 'emargement_relance_formateur', court: 'Relance' },
    { type: 'formation_satisfaction_formateur', court: 'Satisfaction' },
  ],
};

const ENVOIS_AUTOMATIQUES = [
  { cle: 'convocation', court: 'Convocation', long: 'Convocation des participants' },
  { cle: 'rappel', court: 'Rappel', long: 'Rappel avant la formation' },
  // Partis avant la migration 064, quand les délais étaient écrits en dur :
  // affichés seulement s'ils sont partis, ils n'arrivent plus.
  { cle: 'j7', court: 'J-7', long: 'Rappel à J-7', ancien: true },
  { cle: 'j3', court: 'J-3', long: 'Rappel à J-3', ancien: true },
  { cle: 'jour_j', court: 'Jour J matin', long: 'Lien d’émargement du matin (jour J)' },
  { cle: 'jour_j_apres_midi', court: 'Jour J a.-m.', long: 'Lien d’émargement de l’après-midi (jour J)' },
  { cle: 'emargement_relance', court: 'Relance', long: 'Relance des émargements manquants' },
  { cle: 'satisfaction', court: 'Satisfaction', long: 'Questionnaire de satisfaction' },
  { cle: 'cloture', court: 'Clôture', long: 'Clôture du dossier' },
];




const MODALITES = {
  presentiel: 'Présentiel',
  inter_presentiel: 'Inter présentiel',
  distanciel: 'Distanciel',
  mixte: 'Mixte',
};

function periode(jours) {
  if (!Array.isArray(jours) || jours.length === 0) return '—';
  if (jours.length === 1) return formatDate(jours[0]);
  return `${formatDate(jours[0])} → ${formatDate(jours[jours.length - 1])}`;
}

/**
 * Échéance de rappel atteinte : la règle configurée la plus proche dont le
 * délai est déjà couvert. À J−5 avec des règles à J−7 et J−1, c'est « J−7 »
 * qui est due — J−1 ne l'est pas encore.
 */
function echeanceAtteinte(joursAvant, regles) {
  if (joursAvant == null || joursAvant < 0) return null;
  const dues = regles.filter((r) => joursAvant <= r.delai_jours);
  if (dues.length === 0) return null;
  return dues.reduce((a, b) => (a.delai_jours <= b.delai_jours ? a : b));
}

/**
 * Le moment d'un envoi à venir, compté depuis aujourd'hui : « aujourd'hui »,
 * « demain », « dans 3 jours ». Calculé à l'affichage, pour qu'une page
 * ouverte la veille ne dise pas faux le lendemain.
 */
function quandEnvoi(dateIso) {
  if (!dateIso) return '';
  const cible = new Date(`${String(dateIso).slice(0, 10)}T00:00:00`);
  const minuit = new Date();
  minuit.setHours(0, 0, 0, 0);
  const n = Math.round((cible - minuit) / 864e5);
  if (n <= 0) return "aujourd'hui";
  if (n === 1) return 'demain';
  return `dans ${n} jours`;
}

/**
 * Rappels de formation : les sessions à venir et l'échéance de rappel qui
 * s'applique à chacune, d'après les délais configurés dans Paramétrage >
 * Rappels (type 'rappel_formation').
 *
 * À ne pas confondre avec la page Relance, qui porte sur les devis envoyés
 * restés sans réponse.
 */
/**
 * `avecFormateur` : la même liste, restreinte aux journées qui ont une fiche
 * formateur assignée. Ce sont les séances où LUI reçoit quelque chose — et
 * c'est de là qu'on règle ce qu'il lit.
 */
function Rappels({ avecFormateur = false, titre = 'Rappel' }) {
  const [sessions, setSessions] = useState([]);
  // Modifier : la planification du contrat de la ligne ; supprimer : la journée.
  const [contratOuvert, setContratOuvert] = useState(null);
  const [suppression, setSuppression] = useState(null);
  const [suppressionEnCours, setSuppressionEnCours] = useState(false);
  // Type du modèle ouvert dans le panneau, ou null.
  const [modeleEmail, setModeleEmail] = useState(null);
  // Pastille d'envoi cliquée : son nom complet et son état s'affichent
  // sous la grille (au doigt, pas d'infobulle).
  const [detailEnvoi, setDetailEnvoi] = useState(null);
  // Réglage de l'email propre à cette page (cf. ModeleEmailPanneau).
  const [regles, setRegles] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  // Dossier d'où l'on vient (boutons d'accès d'une demande, cf. Avancement) :
  // `cibleContact` peint ses lignes en vert et ne bouge plus, `filtreContact`
  // restreint la liste et se lève depuis le bandeau.
  const [params] = useSearchParams();
  const cibleContact = Number(params.get('contact')) || null;
  const cibleNom = params.get('client') || '';
  const [filtreContact, setFiltreContact] = useState(cibleContact);

  useEffect(() => {
    setFiltreContact(cibleContact);
  }, [cibleContact]);
  const [etat, setEtat] = useState('');
  const [compteurs, setCompteurs] = useState({});
  const [envoi, setEnvoi] = useState('');
  // Période sur la première journée de séance, comme les autres listes.
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [compteursEnvoi, setCompteursEnvoi] = useState({});
  // Date saisie par ligne, tant qu'elle n'est pas enregistrée.

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const charger = useCallback(() => {
    setLoading(true);
    setError(null);
    getSessionsARappeler({
      page,
      limit: LIMIT,
      etat,
      envoi,
      contactId: filtreContact,
      avecFormateur: avecFormateur ? 1 : undefined,
      dateDebut,
      dateFin,
    })
      .then((res) => {
        setSessions(res.data || []);
        setRegles(res.regles || []);
        setTotal(res.pagination?.total ?? (res.data || []).length);
        setCompteurs(res.compteurs || {});
        setCompteursEnvoi(res.compteursEnvoi || {});
      })
      .catch((err) => setError(err.message || 'Impossible de charger les sessions.'))
      .finally(() => setLoading(false));
  }, [page, etat, envoi, filtreContact, avecFormateur, dateDebut, dateFin]);

  useEffect(charger, [charger]);

  const contratDeLaLigne = (s) => ({
    id: s.contrat_id,
    numero: s.contrat_numero,
    societe_nom: s.societe_nom,
    intitule_action: s.intitule_action,
    lieu_formation: s.lieu_formation,
  });

  async function handleSupprimer() {
    if (!suppression) return;
    const cible = suppression;
    setSuppressionEnCours(true);
    try {
      await supprimerSession(cible.id);
      toast.succes(`Journée « ${cible.intitule} » supprimée, avec ses documents.`);
      setSuppression(null);
      charger();
    } catch (err) {
      toast.erreur(err.message || 'Suppression impossible.');
      setSuppression(null);
    } finally {
      setSuppressionEnCours(false);
    }
  }

  /*
   * Ni « Programmer » ni « Envoyer » ici : les rappels J-7 et J-3 sont
   * au planificateur, avec leur propre trace. Les gestes manuels les
   * doublaient, sans qu'aucun des deux ne sache ce que l'autre avait
   * envoyé. Cet écran rend compte de ce qui est parti.
   */

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const rangeStart = total === 0 ? 0 : (page - 1) * LIMIT + 1;
  const rangeEnd = Math.min(page * LIMIT, total);

  return (
    <div className="devis-page">
      <div className="devis-header">
        <div>
          <h1 className="devis-title">{titre}</h1>
        </div>
        {/* Ces rappels partent seuls : la page ne déclenche rien. Mais elle
            donne accès à ce qu'ils DISENT — ceux des élus ici, ceux du
            formateur dans sa vue, jamais les deux mêlés. */}
        <div className="devis-header-actions devis-header-modeles">
          <span className="devis-status-detail">
            Textes {avecFormateur ? 'envoyés au formateur' : 'envoyés aux élus'} :
          </span>
          {(avecFormateur ? MODELES.formateur : MODELES.eleves).map((m) => (
            <button
              key={m.type}
              type="button"
              className="devis-btn devis-btn-sm"
              onClick={() => setModeleEmail(m.type)}
              title={`Objet et contenu de l’email « ${m.court} »`}
            >
              {m.court}
            </button>
          ))}
        </div>
      </div>

      <FiltreDossier
        nom={cibleNom}
        actif={Boolean(filtreContact)}
        total={total}
        onVoirTout={() => {
          setFiltreContact(null);
          setPage(1);
        }}
      />

      <FiltresRepliables libelle="Filtres">
        <div className="devis-toolbar">
          <div className="devis-date-filter">
            <div className="devis-periode">
              <label className="devis-date-field">
                <span>Du</span>
                <input
                  type="date"
                  value={dateDebut}
                  onChange={(e) => {
                    setDateDebut(e.target.value);
                    setPage(1);
                  }}
                  max={dateFin || undefined}
                />
              </label>
              <label className="devis-date-field">
                <span>Au</span>
                <input
                  type="date"
                  value={dateFin}
                  onChange={(e) => {
                    setDateFin(e.target.value);
                    setPage(1);
                  }}
                  min={dateDebut || undefined}
                />
              </label>
            </div>
            {(dateDebut || dateFin) && (
              <button
                type="button"
                className="devis-date-clear"
                onClick={() => {
                  setDateDebut('');
                  setDateFin('');
                  setPage(1);
                }}
              >
                Réinitialiser
              </button>
            )}
            {regles.length > 0 && (
              <span className="devis-pagination-info">
                Délais configurés : {regles.map((r) => `J−${r.delai_jours}`).join(', ')}
              </span>
            )}
          </div>
          <span className="devis-total">{total} session{total > 1 ? 's' : ''}</span>
        </div>

        <StatutsBarre
          titre="Échéance"
          options={FACETTES_RAPPEL}
          compteurs={compteurs}
          valeur={etat}
          total={Object.values(compteurs).reduce((a, n) => a + (Number(n) || 0), 0)}
          libelleTous="Toutes"
          onSelect={(v) => {
            setEtat(v);
            setPage(1);
          }}
        />

        <StatutsBarre
          titre="Envoi"
          options={FACETTES_ENVOI_RAPPEL}
          compteurs={compteursEnvoi}
          valeur={envoi}
          total={Object.values(compteursEnvoi).reduce((a, n) => a + (Number(n) || 0), 0)}
          libelleTous="Tous"
          onSelect={(v) => {
            setEnvoi(v);
            setPage(1);
          }}
        />

      </FiltresRepliables>


      <div className="devis-card">

        <div className="devis-table-wrap">
          {loading && <div className="devis-state">Chargement des sessions...</div>}

          {!loading && error && <div className="devis-state devis-state-error">{error}</div>}

          {!loading && !error && sessions.length === 0 && (
            <div className="devis-empty">
              <div className="devis-empty-title">Aucune session planifiée.</div>
              <div className="devis-empty-subtitle">
                Les sessions se planifient depuis un contrat signé.
              </div>
            </div>
          )}

          {!loading && !error && sessions.length > 0 && (
            <table className="devis-table devis-table-rappels">
              <thead>
                <tr>
                  <th>SESSION</th>
                  <th>CLIENT</th>
                  <th>DATES</th>
                  <th>ÉCHÉANCE · CONVOCATION</th>
                  <th>PARTICIPANTS</th>
                  <th className="devis-th-actions">ACTIONS</th>
                  <th>ENVOIS AUTOMATIQUES</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => {
                  const due = echeanceAtteinte(s.jours_avant, regles);
                  const passee = s.jours_avant < 0;
                  return (
                    <tr
                      key={s.id}
                      className={
                        'devis-row' + (s.contact_id === cibleContact ? ' devis-row-cible' : '')
                      }
                    >
                      <td>
                        <div className="devis-numero">{s.intitule}</div>
                        <div className="devis-contact-email">
                          {[s.groupe_libelle, `contrat ${s.contrat_numero}`]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      </td>
                      <td>
                        <div className="devis-contact-name">{s.societe_nom}</div>
                        <div className="devis-contact-email">
                          {s.prenom} {s.nom} · {s.contact_email}
                        </div>
                      </td>
                      <td>
                        <div>{periode(s.jours)}</div>
                        <div className="devis-contact-email">
                          {[MODALITES[s.modalite] || s.modalite, s.lieu]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`devis-status-pill${
                            passee ? '' : due ? ' devis-pill-refuse' : ' devis-pill-attente'
                          }`}
                        >
                          {passee ? 'Passée' : `dans ${s.jours_avant} j`}
                        </span>
                        {/* Ce que le planificateur enverra, et quand — demandé à
                            lui, pas recalculé ici. Un envoi dont la date est
                            passée n'apparaît plus ; celui qui reste rattrapable
                            s'annonce pour aujourd'hui. Les suivants se lisent
                            au survol, pour garder la ligne courte. */}
                        {(() => {
                          const aVenir = s.envois_a_venir;
                          if (aVenir === null || aVenir === undefined) {
                            return <div className="devis-status-detail">—</div>;
                          }
                          if (!aVenir.length) {
                            return (
                              <div className="devis-status-detail">
                                {passee ? 'tout est parti' : 'plus rien à envoyer'}
                              </div>
                            );
                          }
                          const [prochain, ...suivants] = aVenir;
                          return (
                            <div
                              className="devis-status-detail"
                              title={aVenir
                                .map((e) => `${e.libelle} — ${quandEnvoi(e.date)} (${formatDate(e.date)})`)
                                .join('\n')}
                            >
                              <strong>{prochain.libelle}</strong> — {quandEnvoi(prochain.date)}
                              {suivants.length > 0 && (
                                <span className="rappel-suivants">
                                  {' '}· +{suivants.length} ensuite
                                </span>
                              )}
                            </div>
                          );
                        })()}
                        {/* La convocation se lit avec l'échéance : ce qui
                            part bientôt, et si le document est prêt. */}
                        <div className="rappel-convocation">
                          {s.nb_convocations > 0 ? (
                            <span className="devis-status-pill devis-pill-valide">
                              Convocation envoyée
                            </span>
                          ) : (
                            <span className="devis-status-pill">Convocation à générer</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div>{s.nb_participants}</div>
                        <div className="devis-contact-email">
                          {s.nb_participants_avec_email} avec email
                        </div>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {!avecFormateur && (
                          <ActionsLigne
                            onModifier={() => setContratOuvert(contratDeLaLigne(s))}
                            modifierTitre="Modifier la journée : dates, lieu, participants, formateur"
                            onSupprimer={() => setSuppression(s)}
                            supprimerTitre="Supprimer la journée (ses documents et ses rappels restent)"
                            supprimerDisabled={suppressionEnCours}
                          />
                        )}
                      </td>
                      <td>
                        {/* Toutes les étapes du parcours, dans l'ordre, en
                            pastilles de même taille : ce qui est parti en vert,
                            le reste en gris. La colonne se lit d'un coup d'œil
                            d'une ligne à l'autre, au lieu de pastilles de
                            longueurs inégales qui se replient au hasard. */}
                        {(() => {
                          const envoyes = s.rappels_envoyes || [];
                          const etapes = ENVOIS_AUTOMATIQUES.filter(
                            (e) => !e.ancien || envoyes.includes(e.cle)
                          );
                          const partis = etapes.filter((e) => envoyes.includes(e.cle)).length;
                          return (
                            <>
                              <div className="rappel-envois-grille">
                                {etapes.map((e) => {
                                  const parti = envoyes.includes(e.cle);
                                  const ouvert =
                                    detailEnvoi?.id === s.id && detailEnvoi?.cle === e.cle;
                                  return (
                                    // Un bouton : au doigt, l'infobulle n'existe pas — le
                                    // clic écrit le nom complet et l'état sous la grille.
                                    <button
                                      type="button"
                                      key={e.cle}
                                      className={`rappel-envoi-chip${
                                        parti ? ' rappel-envoi-chip-parti' : ''
                                      }${ouvert ? ' rappel-envoi-chip-actif' : ''}`}
                                      title={`${e.long || e.court} : ${
                                        parti ? 'parti' : 'pas encore parti'
                                      }`}
                                      aria-pressed={ouvert}
                                      onClick={() =>
                                        setDetailEnvoi(ouvert ? null : { id: s.id, cle: e.cle })
                                      }
                                    >
                                      {e.court}
                                    </button>
                                  );
                                })}
                              </div>
                              {detailEnvoi?.id === s.id &&
                                (() => {
                                  const e = etapes.find((x) => x.cle === detailEnvoi.cle);
                                  if (!e) return null;
                                  const parti = envoyes.includes(e.cle);
                                  return (
                                    <div className="rappel-envoi-detail">
                                      <strong>{e.long || e.court}</strong> —{' '}
                                      {parti ? 'parti' : 'pas encore parti'}
                                    </div>
                                  );
                                })()}
                              <div className="devis-status-detail rappel-envois-compte">
                                {partis} / {etapes.length} parti{partis > 1 ? 's' : ''}
                                {!partis && (passee ? ' · rien n’est parti' : ' · en attente')}
                              </div>
                            </>
                          );
                        })()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {!loading && !error && total > 0 && (
          <div className="devis-pagination">
            <span className="devis-pagination-info">
              {rangeStart}-{rangeEnd} sur {total}
            </span>
            <div className="devis-pagination-controls">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Précédent
              </button>
              <span className="devis-pagination-page">
                Page {page} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {modeleEmail && (
        <ModeleEmailPanneau type={modeleEmail} onClose={() => setModeleEmail(null)} />
      )}

      {contratOuvert && (
        <SessionsPanel
          contrat={contratOuvert}
          onClose={() => {
            setContratOuvert(null);
            charger();
          }}
        />
      )}

      {suppression && (
        <ConfirmationSuppression
          titre="Supprimer cette journée ?"
          sousTitre={`${suppression.intitule} · ${suppression.contrat_numero} · ${suppression.societe_nom || ''}`}
          details={[
            'La journée et ses dates.',
          ]}
          note="Sa convocation, sa feuille de présence, ses attestations, ses rappels, le contrat et ses autres journées restent. Restaurable depuis la corbeille."
          enCours={suppressionEnCours}
          onAnnuler={() => setSuppression(null)}
          onConfirmer={handleSupprimer}
        />
      )}

    </div>
  );
}

export default Rappels;
